export const RELATIVE_WINDS = [
  'in_face',
  'front_right',
  'from_right',
  'back_right',
  'at_back',
  'back_left',
  'from_left',
  'front_left',
] as const

export type RelativeWind = (typeof RELATIVE_WINDS)[number]

export type WindFavorability = 'favorable' | 'crosswind' | 'headwind'

export type RelativeWindIndicator = {
  relativeWind: RelativeWind
  facing: string
  label: string
  favorability: WindFavorability
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

const SIXTEEN_POINT_COMPASS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
]

const DEGREES_PER_SIXTEEN_POINT = 22.5

const BIDIRECTIONAL_FACINGS: Record<string, [string, string]> = {
  'N/S': ['N', 'S'],
  'E/W': ['E', 'W'],
  'NE/SE': ['NE', 'SE'],
  'NW/SW': ['NW', 'SW'],
}

const FAVOURABILITY: Record<RelativeWind, number> = {
  at_back: 0,
  back_right: 1,
  back_left: 1,
  from_right: 2,
  from_left: 2,
  front_right: 3,
  front_left: 3,
  in_face: 4,
}

const HEADWIND_RELATIVE_WINDS: ReadonlySet<RelativeWind> = new Set<RelativeWind>([
  'in_face',
  'front_left',
  'front_right',
])

const CROSSWIND_RELATIVE_WINDS: ReadonlySet<RelativeWind> = new Set<RelativeWind>([
  'from_left',
  'from_right',
])

const RELATIVE_WIND_LABELS: Record<RelativeWind, string> = {
  in_face: 'Wind in your face',
  front_right: 'Wind off your front right',
  from_right: 'Wind off your right shoulder',
  back_right: 'Wind off your back right',
  at_back: 'Wind at your back',
  back_left: 'Wind off your back left',
  from_left: 'Wind off your left shoulder',
  front_left: 'Wind off your front left',
}

export function cardinalToDegrees(cardinal: string | undefined): number | undefined {
  if (cardinal === undefined) return undefined
  const index = SIXTEEN_POINT_COMPASS.indexOf(cardinal.trim().toUpperCase())
  return index < 0 ? undefined : index * DEGREES_PER_SIXTEEN_POINT
}

export function degreesToCardinal(degrees: number): string {
  const normalized = ((degrees % 360) + 360) % 360
  const index = Math.round(normalized / 45) % 8
  return COMPASS[index]
}

export function relativeWindForFacing(
  facing: string,
  windFrom: string,
): RelativeWind | undefined {
  const facingIndex = COMPASS.indexOf(facing)
  const windIndex = COMPASS.indexOf(windFrom)
  if (facingIndex === -1 || windIndex === -1) return undefined
  return RELATIVE_WINDS[(windIndex - facingIndex + 8) % 8]
}

export function resolveRelativeWind(
  directionFacing: string,
  windFrom: string,
): RelativeWind | undefined {
  const facings = BIDIRECTIONAL_FACINGS[directionFacing] ?? [directionFacing]
  const options = facings
    .map((facing) => relativeWindForFacing(facing, windFrom))
    .filter((value): value is RelativeWind => value !== undefined)
  if (options.length === 0) return undefined
  return options.reduce((best, value) =>
    FAVOURABILITY[value] < FAVOURABILITY[best] ? value : best,
  )
}

export function relativeWindLabel(relativeWind: RelativeWind): string {
  return RELATIVE_WIND_LABELS[relativeWind]
}

export function relativeWindFavorability(relativeWind: RelativeWind): WindFavorability {
  if (HEADWIND_RELATIVE_WINDS.has(relativeWind)) return 'headwind'
  if (CROSSWIND_RELATIVE_WINDS.has(relativeWind)) return 'crosswind'
  return 'favorable'
}

export function resolveWindIndicator(
  directionFacing: string | null | undefined,
  windDegree: number | null | undefined,
): RelativeWindIndicator | null {
  if (directionFacing == null || windDegree == null) return null
  const facing = directionFacing.trim()
  if (facing === '') return null

  const windFrom = degreesToCardinal(windDegree)
  const facings = BIDIRECTIONAL_FACINGS[facing] ?? [facing]

  let best: RelativeWindIndicator | null = null
  for (const leg of facings) {
    const relativeWind = relativeWindForFacing(leg, windFrom)
    if (relativeWind === undefined) continue
    if (best === null || FAVOURABILITY[relativeWind] < FAVOURABILITY[best.relativeWind]) {
      best = {
        relativeWind,
        facing: leg,
        label: relativeWindLabel(relativeWind),
        favorability: relativeWindFavorability(relativeWind),
      }
    }
  }
  return best
}
